import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { LEVELS } from "./questions";
import { LevelBody } from "./LevelBody";

const initial = { name: "", phone: "", business_name: "", niche: "", competitor: "", platform: "Instagram", goal: "Get more customers" };

const validators = [
  (f) => (f.name.trim() && /^[+\d\s-]{7,20}$/.test(f.phone.trim()) ? null : "Add your name and a valid phone number"),
  (f) => (f.business_name.trim() && f.niche.trim() ? null : "Add your business name and niche"),
  (f) => (f.competitor.trim() ? null : "Tell us at least one competitor"),
  () => null,
];

export const GameWizard = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const next = async () => {
    const err = validators[step](form);
    if (err) return toast.error(err);
    if (step < LEVELS.length - 1) return setStep(step + 1);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/plans`, form);
      navigate(`/plan/${data.id}`);
    } catch {
      toast.error("Oops, couldn't start your plan. Try again!");
      setLoading(false);
    }
  };

  const lvl = LEVELS[step];
  return (
    <section id="game" data-testid="game-wizard" className="px-6 md:px-12 py-16">
      <div className="max-w-3xl mx-auto bg-white rounded-[32px] border border-[var(--line)] shadow-[0_24px_60px_-30px_rgba(16,46,36,.45)] p-6 md:p-12">
        <div className="flex items-center justify-between mb-8">
          <span data-testid="game-level-label" className="font-hand text-2xl text-[var(--green-2)]">{lvl.emoji}</span>
          <div className="flex gap-1.5">
            {LEVELS.map((_, i) => (
              <span key={i} className={`h-2.5 rounded-full transition-[width,background-color] duration-500 ${i <= step ? "bg-[var(--green)] w-10" : "bg-[var(--line)] w-5"}`} />
            ))}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: .3 }}>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">{lvl.title}</h2>
            <p className="text-[var(--muted)] mt-2 mb-8">{lvl.sub}</p>
            <LevelBody step={step} form={form} set={set} onEnter={next} />
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center justify-between mt-12">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || loading} data-testid="game-back-btn"
            className="flex items-center gap-2 text-sm font-semibold disabled:opacity-30 hover:-translate-x-1 transition-transform">
            <ArrowLeft size={16} /> Back
          </button>
          <button onClick={next} disabled={loading} data-testid="game-next-btn"
            className="rounded-full bg-[var(--green)] text-[var(--cream)] px-7 py-3.5 font-semibold flex items-center gap-2 hover:bg-[var(--green-2)] transition-colors disabled:opacity-70">
            {loading ? <><Loader2 className="animate-spin" size={18} /> Starting...</>
              : step === LEVELS.length - 1 ? <><Trophy size={18} /> Reveal my calendar</> : <>Next level <ArrowRight size={18} /></>}
          </button>
        </div>
      </div>
    </section>
  );
};
